import { localAuthRedirect } from '../../utils/auth-redirect';
/**
 * GuardedRoute Component
 * 
 * Protects routes based on authentication status and role permissions.
 * 
 * Role Hierarchy (from lowest to highest privileges):
 * - 'user': Base level access
 * - 'support': Mid-level access, includes all 'user' permissions
 * - 'admin': Highest level access, includes all 'support' and 'user' permissions
 * 
 * When 'requiredRole' is specified:
 * - If user has no role, access is denied
 * - If user's role is equal to or higher than the required role in hierarchy, access is granted
 *   (e.g., an 'admin' can access routes requiring 'support' or 'user' roles)
 * - If a role is not in the known hierarchy, an exact match is required
 */
import BoardsLoader from '@common-components/tasks-panel/recents-boards/boards-loader';
import { User, UserInterface } from '@common-models/user';
import { Stack } from '@mui/material';
import { isBusyAuthStatus, isFailAuthStatus, isSuccessAuthStatus } from '@common-reducers/UserReducer';
import { selectUser } from '@common-reducers/UserSelectors';
import { useSelector } from 'react-redux';
import { CommonRootState } from '@common/types/common-root-state-type';
import { namespace } from '@common/config/constants';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { authService } from '@/common/bt-auth/auth-service';
import { debugLog } from '@common/utils/debug-logger';
import { decideProtectedRoute } from './guarded-route-decision';
import BackendUnreachable from './backend-unreachable';
import { resetAuthRetryAttempts } from '@/common/bt-auth/auth-retry-policy';
import { flowPath, isAuthPath } from '@common/utils/clear-flow';

// Define role hierarchy - higher index means higher privilege
const RoleHierarchy = ['user', 'support', 'admin'];

// Check if user role has required permission
const hasRequiredRole = (userRole?: string, requiredRole?: string): boolean => {
  if (!requiredRole) return true; // No role required
  if (!userRole) return false; // User has no role
  
  const userRoleIndex = RoleHierarchy.indexOf(userRole);
  const requiredRoleIndex = RoleHierarchy.indexOf(requiredRole);
  
  // If role is not in hierarchy, exact match is required
  if (userRoleIndex === -1 || requiredRoleIndex === -1) {
    return userRole === requiredRole;
  }
  
  // Check if user role is high enough in hierarchy
  return userRoleIndex >= requiredRoleIndex;
};

// Build the `?redirect=<current path>` search string used when bouncing an unauthenticated
// visitor to /login, so they return to where they were after signing in. Returns '' when
// redirect-back is disabled. Reads window fresh so it reflects the current location.
//
// Skips the param when the current path is ITSELF an auth screen: on logout the thunk
// navigates to the login page first, and the protected route it just left then re-renders
// and bounces from there — which produced a self-referential `/login?redirect=/login`.
const buildLoginRedirectSearch = (redirectBack: boolean): string => {
  if (!redirectBack || isAuthPath()) return '';
  const params = new URLSearchParams(window.location.search);
  params.set('redirect', window.location.pathname + window.location.search);
  return `?${params.toString()}`;
};

// Resolve where to send an already-authenticated user who landed on a public-only route
// (e.g. /login). Precedence: explicit ?redirect= param → the route's `redirect` prop → /calendar
// (or its clear-flow counterpart, so a clear auth screen never falls back into the full product).
const resolveRedirectTarget = (
  searchParams: URLSearchParams,
  redirect: GuardedRouteProps['redirect'],
  user?: UserInterface,
): string => {
  const urlRedirect = localAuthRedirect(searchParams);
  if (urlRedirect) return urlRedirect;
  if (typeof redirect === 'string' && redirect) return redirect;
  if (typeof redirect === 'function' && user) {
    try { return redirect(new User(user)); } catch { /* fall through to default */ }
  }
  return flowPath('/calendar');
};

export interface GuardedRouteProps {
    element: React.ReactElement;
    auth?: boolean;
    redirect?: string | ((user: User) => string);
    requiredRole?: string;
    redirectBack?: boolean;
    /**
     * Where to bounce an unauthenticated visitor. Defaults to the regular `/login`;
     * the `/clear-*` flow passes `/clear-login` so it stays self-contained.
     */
    loginPath?: string;
}

// Full-screen centered auth loader, shared by the "authenticating" and "finalizing" states.
const AuthLoader = ({ message }: { message: string }) => (
  <Stack sx={{ width: '100vw', height: '100vh' }} justifyContent="center" alignItems="center">
    <BoardsLoader loadingMessage={message} />
  </Stack>
);

export default function GuardedRoute({ element, auth, redirect, requiredRole, redirectBack = true, loginPath = '/login' }: GuardedRouteProps) {
  useTranslation(namespace);
  const authenticationStatus = useSelector((state: CommonRootState) => state.UserReducer.authenticationStatus);
  const authFailureKind = useSelector((state: CommonRootState) => state.UserReducer.authFailureKind);
  const userId = useSelector((state: CommonRootState) => state.UserReducer.userId);
  const user = useSelector((state: CommonRootState) => selectUser(state));
  // Reactive search params — react-router renders the SAME GuardedRoute element for a path
  // regardless of query string, so a mount-time `new URLSearchParams(window.location.search)`
  // snapshot goes stale if `?redirect=` arrives after mount (e.g. a guard on /tasks redirects
  // to /login?redirect=/tasks while /login is already active). That stale read made the
  // post-login redirect fall back to /calendar instead of honoring ?redirect=. useSearchParams
  // always reflects the current location.
  const [searchParams] = useSearchParams();

  const isBusy = isBusyAuthStatus(authenticationStatus);
  const isSuccess = isSuccessAuthStatus(authenticationStatus);
  const isFail = isFailAuthStatus(authenticationStatus);
  const isAuthenticated = !!userId && !!user;
  const hasToken = authService.hasStoredToken();

  // A hydrated session means any earlier unreachable-backend streak is over — start the next
  // one from a full retry budget.
  useEffect(() => {
    if (isAuthenticated) resetAuthRetryAttempts();
  }, [isAuthenticated]);

  debugLog('login-flow', 'GuardedRoute: render', { path: window.location.pathname, auth, requiredRole, userId, authenticationStatus, authFailureKind });

  // ---- Protected routes (auth === true) ----
  // The branch order + reasoning live in `decideProtectedRoute` (pure, unit-tested).
  if (auth) {
    const decision = decideProtectedRoute({
      hasToken,
      hasUserId: !!userId,
      hasUser: !!user,
      isBusy,
      isSuccess,
      isFail,
      failureKind: authFailureKind,
    });
    debugLog('login-flow', 'GuardedRoute: protected-route decision', { decision, authenticationStatus, authFailureKind, userId });

    if (decision.type === 'redirect-login') {
      const search = buildLoginRedirectSearch(redirectBack);
      return <Navigate to={{ pathname: loginPath, search }} replace={true} />;
    }
    if (decision.type === 'loader') {
      return <AuthLoader message={decision.message === 'authenticating' ? 'Authenticating...' : 'Finalizing sign-in...'} />;
    }
    // Backend unreachable with a stored token: hold the session and retry — never a logout.
    if (decision.type === 'backend-unreachable') {
      return <BackendUnreachable loginSearch={buildLoginRedirectSearch(redirectBack)} loginPath={loginPath} />;
    }
    // 'render' falls through to the role gate below.
  }

  // ---- Public-only routes (auth === false) ----

  // Already authenticated (by status or hydrated user) → redirect away from the public route.
  if (auth === false && (isSuccess || user)) {
    const target = resolveRedirectTarget(searchParams, redirect, user);
    debugLog('login-flow', 'GuardedRoute: public route while authenticated, redirecting', { target });
    return <Navigate to={target} replace={true} />;
  }

  // ---- Role gate ----
  if (requiredRole && user && !hasRequiredRole(user.role, requiredRole)) {
    const target = redirect ? (typeof redirect === 'string' ? redirect : redirect(new User(user))) : loginPath;
    debugLog('login-flow', 'GuardedRoute: insufficient role, redirecting', { target });
    return <Navigate to={target} replace={true} />;
  }

  debugLog('login-flow', 'GuardedRoute: access granted, rendering element');
  return element;
}
